import { useMemo } from 'react';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import type { NullableDateInput } from '@localization/formatters/dateFormatter';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { resolveEventDateDisplay } from './resolveEventDateDisplay';
import { eventDateMetadataToContent } from './eventDateMetadata';

type UseEventLevelDateDisplayArgs = Readonly<{
  // Range START — the dedicated EventInfo.startDate override. When null the range is suppressed (see below):
  // callers must NOT substitute the canonical event date, which is a date within the event, not its start.
  startTimestamp: NullableDateInput;
  timezone?: string | null;
  // Event-level date config: EventInfo.dateMetadata and EventInfo.endDate.timestamp (PRNT-4548).
  dateMetadata?: Parameters<typeof eventDateMetadataToContent>[0];
  endDateTimestamp?: string | null;
}>;

// Resolves the event-level multi-day date override (a saved range / custom text) from EventInfo. Flag-gated
// behind websiteMultiDayEventDateEnabled: returns undefined when the flag is off (or no range is configured),
// so callers fall back to their existing single-date string (purely additive). This deliberately gates
// *display* on the flag as a rollout safety valve — a stricter stance than the "saved data always renders"
// convention — so a segment can be rolled back cleanly while the feature stabilizes; drop the gate once the
// flag is retired. Unlike useWelcomeEventDateDisplay this reads only the event-level source (no welcome-page
// blob fetch), so it is usable from both admin and guest.
export const useEventLevelDateDisplay = ({ startTimestamp, timezone, dateMetadata, endDateTimestamp }: UseEventLevelDateDisplayArgs): string | undefined => {
  const [isMultiDayEnabled] = useIsFeatureEnabled('websiteMultiDayEventDateEnabled');
  const { formatDate, formatEventDateRange } = useEventLocaleFormatter();
  return useMemo(() => {
    if (!isMultiDayEnabled || dateMetadata?.dateDisplayType !== 'dateRange') {
      return undefined;
    }
    const content = eventDateMetadataToContent(dateMetadata, endDateTimestamp);
    // Without a real start we can't form a correct range — the canonical event date is a date *within* the
    // event, not its start, so fabricating a start from it would show a wrong range. Return undefined so the
    // caller falls back to the single date. Custom text carries no dates, so it still renders.
    if (startTimestamp == null && content.dateRangeFormat !== 'custom') {
      return undefined;
    }
    return resolveEventDateDisplay(content, startTimestamp, { titlecase: true, timezone }, { formatDate, formatEventDateRange });
  }, [isMultiDayEnabled, dateMetadata, endDateTimestamp, startTimestamp, timezone, formatDate, formatEventDateRange]);
};
